import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { size } from 'lodash';
import { NEVER, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { SvgIcon } from '../../../../../../../cross-team-shared/icons/create-icon-definition';
import { keys } from '../../../../../../../cross-team-shared/misc-utils/keys';
import { getOrThrow } from '../../../../../../../cross-team-shared/misc-utils/strict-record';
import { VectorLayerEditMode } from '../../../../../../../cross-team-shared/types/tool-modes';
import { addModeIcon } from '../../../../../../shared/icons/toolbars/add-mode-icon';
import { dragModeIcon } from '../../../../../../shared/icons/toolbars/drag-mode-icon';
import { removeModeIcon } from '../../../../../../shared/icons/toolbars/remove-mode-icon';
import {
  filterSelectionByOutput,
  RootShapeSelectionService,
} from '../../../utils/root-shape-selection.service';
import { AbstractToolModeSecondaryToolbar } from '../abstract-tool-mode-secondary-toolbar';

const incrementalShapeTypes = new Set(['polygon', 'line', 'arrow']);

@Component({
  selector: 'h3-edit-tool-2d-secondary-toolbar',
  templateUrl: './edit-tool-2d-secondary-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditTool2dSecondaryToolbarComponent extends AbstractToolModeSecondaryToolbar {
  @Input({ required: true }) videoOutputName!: string;

  private readonly selectionService = inject(RootShapeSelectionService);

  buttons: Array<{
    value: VectorLayerEditMode;
    icon: SvgIcon;
    tooltip: string;
  }> = [
    {
      value: 'drag',
      icon: dragModeIcon,
      tooltip: 'Select point',
    },
    {
      value: 'add',
      icon: addModeIcon,
      tooltip: `Add point (${this.keyboardShortcutService.humanReadableModifier('shiftKey')})`,
    },
    {
      value: 'remove',
      icon: removeModeIcon,
      tooltip: `Remove point (${this.keyboardShortcutService.humanReadableModifier('altKey')})`,
    },
  ];
  isIncrementalShapeFocused$: Observable<boolean>;

  protected override toolName = 'Edit' as const;

  constructor() {
    super();
    this.isIncrementalShapeFocused$ = this.getIsIncrementalShapeFocused$();

    this.subscribeToSelectEditToolShortcut();
    this.subscribeToResetToolMode();
  }

  private subscribeToSelectEditToolShortcut() {
    const orderedModes = this.buttons.map((button) => button.value);
    this.subscribeTo(
      this.isIncrementalShapeFocused$.pipe(
        switchMap((isIncrementalShapeFocused) =>
          isIncrementalShapeFocused
            ? this.keyboardShortcutService.getEvent$('selectEditTool')
            : NEVER,
        ),
      ),
      () => {
        this.cycleToNextMode(orderedModes);
      },
    );
  }

  private subscribeToResetToolMode() {
    const shouldResetToolMode$ = this.isIncrementalShapeFocused$.pipe(
      filter((isIncrementalShapeFocused) => !isIncrementalShapeFocused),
    );
    this.subscribeTo(shouldResetToolMode$, () => {
      this.setMode('drag');
    });
  }

  private getIsIncrementalShapeFocused$() {
    return this.getInput$('videoOutputName').pipe(
      switchMap(() =>
        this.selectionService.getSelection$().pipe(
          map((selection) => {
            const output = getOrThrow(
              this.taskWorkspaceStore.state().outputAnnotationInfo,
              this.videoOutputName,
            );
            return filterSelectionByOutput(output, selection);
          }),
          distinctUntilChanged(),
          map((selection) => {
            if (size(selection) !== 1) {
              return false;
            }
            const shapeStore = this.taskWorkspaceStore.getShapeStore(
              this.videoOutputName,
              keys(selection)[0],
            );
            const shape = shapeStore.state();
            return (
              shape.outputName === this.videoOutputName &&
              incrementalShapeTypes.has(shape.type)
            );
          }),
        ),
      ),
    );
  }
}
